import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { StreamingCommand } from '../../aiStreamingOrchestratorPluginType';
import { findNodeByLocalIdAndDoc } from './find-node-by-local-id';

/**
 * The set of command types whose targeted subtree is genuinely
 * removed from the doc. For these, descendant `localId`s of the
 * target are also expected to disappear from the post-apply snapshot
 * and must be added to the exclusion set so they don't fire
 * `unexpected_node_removal` spuriously.
 *
 * Commands like `moveNode`, `wrap`, and `wrapNode` are deliberately
 * NOT in this set: they relocate/nest the targeted subtree but the
 * descendants remain in the doc. If a descendant of a moved or
 * wrapped node disappears, that IS a genuine anomaly worth surfacing
 * — so we leave it to `unexpected_node_removal` to fire.
 */
const COMMAND_TYPES_THAT_REMOVE_SUBTREE: ReadonlySet<StreamingCommand['type']> = new Set([
	'removeNode',
	'removeNodes',
	'replaceNode',
]);

/**
 * Returns the set of `localId`s whose disappearance from the full-depth
 * document snapshot after a streaming command's apply is *expected*.
 * These nodes are excluded from the `unexpected_node_removal` diagnostic
 * so the event only fires for genuinely collateral removals.
 *
 * The set always includes the directly-targeted `localId`s. When
 * `preApplyDoc` is provided AND the command's intent is to genuinely
 * remove the targeted subtree (`removeNode`, `removeNodes`,
 * `replaceNode`), it ALSO includes every descendant `localId` of those
 * targets — this is required because the `unexpected_node_removal`
 * diagnostic walks the doc recursively (`collectAllLocalIds`), so
 * removing a container with nested `localId`-bearing children would
 * otherwise cause every descendant to fire the diagnostic spuriously.
 *
 * For commands that preserve the targeted subtree (`moveNode`, `wrap`,
 * `wrapNode`) the descendants are NOT expanded — those descendants
 * must still appear in the post-apply doc and if any of them
 * disappears, that IS a genuine anomaly we want `unexpected_node_removal`
 * to fire on.
 *
 * The set is deliberately **over-inclusive** for v1 — false negatives
 * (missed unexpected removals) are preferable to false positives
 * (spurious noise) until dashboards are built and we can measure the
 * actual signal-to-noise ratio. Specifically:
 *
 * - `removeNode`  — removes a single node by `localId`. The localId
 *                   should disappear.
 * - `removeNodes` — removes a list of nodes by `localIds`. All listed
 *                   localIds should disappear.
 * - `moveNode`   — relocates a node. Its `localId` may legitimately
 *                   leave the top-level snapshot if the move target is
 *                   nested.
 * - `replaceNode` — replaces a node's content. The targeted `localId`
 *                   may or may not be preserved on the replacement node
 *                   depending on downstream apply logic. Suppress the
 *                   diagnostic to avoid false positives whenever the
 *                   localId is not preserved (the common
 *                   "replace this paragraph with new content" path
 *                   from the AI legitimately drops the original
 *                   localId).
 * - `wrap` / `wrapNode` — wrap the targeted node inside a new node.
 *                   If the wrapper sits at top level, the wrapped
 *                   node's `localId` leaves the top-level snapshot
 *                   (it's now nested). Suppress the diagnostic to
 *                   avoid false positives for "wrap this in a panel"
 *                   etc.
 *
 * Trade-off: a `replaceNode` / `wrap` that *also* drops an unrelated
 * top-level neighbour will silently swallow that collateral removal
 * for the targeted localId only — the neighbour's localId is still
 * detected. Acceptable for v1.
 *
 * If new removal-style commands are added to `StreamingCommand` in the
 * future, extend this function so the diagnostic does not start firing
 * spuriously for them.
 */
export function extractExplicitRemovalTargets(
	command: StreamingCommand,
	preApplyDoc?: PMNode,
): Set<string> {
	const ids = new Set<string>();
	switch (command.type) {
		case 'removeNode':
		case 'moveNode':
		case 'replaceNode':
			ids.add(command.localId);
			break;
		case 'removeNodes':
			for (const id of command.localIds) {
				ids.add(id);
			}
			break;
		case 'wrap':
		case 'wrapNode':
			ids.add(command.localId);
			break;
		default:
			// All other commands either target a single node by `localId`
			// without removing it (insertNodeBefore / insertNodeAfter /
			// setAttrs) or have no target at all (appendNode /
			// appendNodeToEnd).
			break;
	}

	// Expand each direct target to include its descendant `localId`s
	// from the pre-apply doc, but ONLY for commands whose intent is to
	// remove the targeted subtree entirely. For moveNode/wrap/wrapNode
	// the subtree is preserved (relocated or nested), so its
	// descendants must still appear in the post-apply snapshot — if
	// they don't, that is a genuine anomaly we want to surface.
	if (
		preApplyDoc !== undefined &&
		ids.size > 0 &&
		COMMAND_TYPES_THAT_REMOVE_SUBTREE.has(command.type)
	) {
		// Snapshot the direct targets up-front because we're about to
		// mutate `ids` while iterating.
		const directTargets = Array.from(ids);
		for (const targetId of directTargets) {
			const result = findNodeByLocalIdAndDoc(preApplyDoc, targetId);
			if (!result) {
				continue;
			}
			result.node.descendants((descendant) => {
				const descendantLocalId = descendant.attrs?.localId;
				if (typeof descendantLocalId === 'string' && descendantLocalId.length > 0) {
					ids.add(descendantLocalId);
				}
				return true;
			});
		}
	}

	return ids;
}
